import { KPICard } from "@/components/accounting/kpi-card";
import { MoneyDisplay } from "@/components/accounting/money-display";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import {
	DecisionStatus,
	PeriodStatus,
	Side,
} from "@/gen/omnios/v1/accounting_pb";
import {
	useAccountsByPrefix,
	useApproveDecision,
	useGetAutonomyConfig,
	useGetCloseChecklist,
	useGetTrialBalance,
	useListDecisions,
	useListEntries,
	useListPeriods,
	useRejectDecision,
} from "@/hooks/use-accounting";
import { formatDate } from "@/lib/format";
import { useOrganizationStore } from "@/lib/organization";
import { getRecentItems } from "@/lib/recent-accounting";

import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	AlertTriangle,
	ArrowRight,
	Banknote,
	Bot,
	CheckCircle2,
	Circle,
	Clock,
	FileText,
	ShieldCheck,
	XCircle,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/")({
	component: AccountingDashboard,
});

const AUTONOMY_LABELS: Array<{
	key: string;
	field: "postingLevel" | "reconLevel" | "closeLevel" | "complianceLevel";
}> = [
	{ key: "autonomy_posting", field: "postingLevel" },
	{ key: "autonomy_recon", field: "reconLevel" },
	{ key: "autonomy_close", field: "closeLevel" },
	{ key: "autonomy_compliance", field: "complianceLevel" },
];

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

// ---------------------------------------------------------------------------
// Aging mini-bar — stacked horizontal bar showing aging distribution
// ---------------------------------------------------------------------------

function AgingBar({
	current,
	d30,
	d60,
	d90,
	d90plus,
}: {
	current: number;
	d30: number;
	d60: number;
	d90: number;
	d90plus: number;
}) {
	const total = current + d30 + d60 + d90 + d90plus;
	if (total === 0) return null;
	const pct = (v: number) => `${((v / total) * 100).toFixed(1)}%`;
	const segments = [
		{ value: current, color: "bg-green-500", label: "current" },
		{ value: d30, color: "bg-amber-400", label: "1-30" },
		{ value: d60, color: "bg-orange-500", label: "31-60" },
		{ value: d90, color: "bg-red-400", label: "61-90" },
		{ value: d90plus, color: "bg-red-600", label: "90+" },
	];
	return (
		<div className="flex h-1.5 w-full rounded-full overflow-hidden mt-2">
			{segments
				.filter((s) => s.value > 0)
				.map((s) => (
					<div
						key={s.label}
						className={`${s.color} transition-all`}
						style={{ width: pct(s.value) }}
						title={`${s.label}: ${formatAmount(s.value)}`}
					/>
				))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Period close progress ring
// ---------------------------------------------------------------------------

function ProgressRing({
	percent,
	size = 48,
}: { percent: number; size?: number }) {
	const r = (size - 6) / 2;
	const circumference = 2 * Math.PI * r;
	const offset = circumference - (percent / 100) * circumference;
	return (
		<svg width={size} height={size} className="shrink-0">
			<circle
				cx={size / 2}
				cy={size / 2}
				r={r}
				fill="none"
				stroke="currentColor"
				className="text-muted/20"
				strokeWidth={4}
			/>
			<circle
				cx={size / 2}
				cy={size / 2}
				r={r}
				fill="none"
				stroke="currentColor"
				className="text-primary transition-all"
				strokeWidth={4}
				strokeDasharray={circumference}
				strokeDashoffset={offset}
				strokeLinecap="round"
				transform={`rotate(-90 ${size / 2} ${size / 2})`}
			/>
			<text
				x={size / 2}
				y={size / 2}
				textAnchor="middle"
				dominantBaseline="central"
				className="fill-foreground text-[10px] font-semibold"
			>
				{percent}%
			</text>
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

function AccountingDashboard() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const allEntities = useOrganizationStore((s) => s.allEntities);
	const { data: periods } = useListPeriods();

	const currentPeriod = periods?.periods.find(
		(p) => p.status === PeriodStatus.OPEN,
	);
	const periodId = currentPeriod?.periodId ?? "";

	const { data: trialBalance, isLoading } = useGetTrialBalance(
		periodId,
		"accrual",
	);
	const { data: decisionsData } = useListDecisions({
		status: DecisionStatus.PROPOSED,
	});
	const { data: entriesData, isLoading: entriesLoading } = useListEntries({
		page: { pageSize: 8 },
	});
	const { data: autonomyConfig } = useGetAutonomyConfig();
	const { data: closeChecklist } = useGetCloseChecklist(periodId);

	// AR/AP data for aging bars
	const { lines: arLines } = useAccountsByPrefix("62");
	const { lines: apLines } = useAccountsByPrefix("60");

	const approveMutation = useApproveDecision();
	const rejectMutation = useRejectDecision();

	// ── KPI calculations ──
	const revenue =
		trialBalance?.lines
			.filter(
				(l) =>
					l.normalBalance === Side.CREDIT && l.accountCode?.startsWith("9"),
			)
			.reduce((sum, l) => sum + Number.parseFloat(l.net || "0"), 0) ?? 0;

	const expenses =
		trialBalance?.lines
			.filter(
				(l) =>
					l.normalBalance === Side.DEBIT &&
					(l.accountCode?.startsWith("2") || l.accountCode?.startsWith("44")),
			)
			.reduce((sum, l) => sum + Number.parseFloat(l.net || "0"), 0) ?? 0;

	const cash =
		trialBalance?.lines
			.filter((l) => l.accountCode?.startsWith("5"))
			.reduce((sum, l) => sum + Number.parseFloat(l.net || "0"), 0) ?? 0;

	const profit = revenue - expenses;

	const arTotal = arLines.reduce(
		(s, l) => s + Number.parseFloat(l.net || "0"),
		0,
	);
	const apTotal = apLines.reduce(
		(s, l) => s + Math.abs(Number.parseFloat(l.net || "0")),
		0,
	);

	const decisions = decisionsData?.decisions ?? [];
	const entries = entriesData?.entries ?? [];

	// Period close progress
	const closeSteps = closeChecklist?.steps ?? [];
	const closeTotal = closeSteps.length;
	const closeDone = closeSteps.filter(
		(s) => s.status === "completed" || s.status === "skipped",
	).length;
	const closePercent =
		closeChecklist?.progressPct != null
			? Math.round(closeChecklist.progressPct)
			: closeTotal > 0
				? Math.round((closeDone / closeTotal) * 100)
				: 0;

	const recentItems = useMemo(() => getRecentItems().slice(0, 5), []);

	const dashboardEntryCols = useMemo<ColumnDef<(typeof entries)[number]>[]>(
		() => [
			{
				accessorKey: "entryNumber",
				header: "№",
				cell: ({ row }) => (
					<span className="font-mono text-muted-foreground">
						{row.original.entryNumber}
					</span>
				),
				size: 80,
			},
			{
				accessorKey: "validDate",
				header: t("entry_date"),
				cell: ({ row }) =>
					row.original.validDate
						? formatDate(row.original.validDate.toDate(), "dd.MM.yyyy")
						: "—",
				size: 90,
			},
			{
				accessorKey: "description",
				header: t("entry_description"),
				cell: ({ row }) => (
					<span className="truncate block max-w-[200px]">
						{row.original.description || "—"}
					</span>
				),
			},
			{
				accessorKey: "status",
				header: t("entry_status"),
				cell: ({ row }) => (
					<EntryStatusDot status={row.original.status} t={t} />
				),
				size: 90,
			},
			{
				id: "debit",
				header: () => (
					<span className="text-right block">{t("line_debit")}</span>
				),
				cell: ({ row }) => {
					const d = row.original.lines
						.filter((l) => l.side === 1)
						.reduce((s, l) => s + Number.parseFloat(l.amount || "0"), 0);
					return d > 0 ? (
						<span className="text-right block">
							<MoneyDisplay amount={String(d)} />
						</span>
					) : null;
				},
				size: 100,
			},
			{
				id: "credit",
				header: () => (
					<span className="text-right block">{t("line_credit")}</span>
				),
				cell: ({ row }) => {
					const c = row.original.lines
						.filter((l) => l.side === 2)
						.reduce((s, l) => s + Number.parseFloat(l.amount || "0"), 0);
					return c > 0 ? (
						<span className="text-right block">
							<MoneyDisplay amount={String(c)} />
						</span>
					) : null;
				},
				size: 100,
			},
		],
		[t],
	);

	// Count items needing attention
	const draftCount = entries.filter((e) => e.status === 1).length;
	const pendingDecisions = decisions.length;

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			...(periodId
				? [
						{
							id: "period",
							content: `${t("period")}: ${periodId}`,
							align: "left" as const,
						},
					]
				: []),
			{
				id: "info",
				content: allEntities
					? t("consolidated_mode")
					: t("row_count", { count: entries.length }),
				align: "left" as const,
			},
		],
		[periodId, allEntities, entries.length, t],
	);
	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* ── Row 1: KPI cards ── */}
			<div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-border border-b shrink-0">
				{isLoading ? (
					Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-[88px] rounded-lg" />
					))
				) : (
					<>
						<KPICard
							label={t("kpi_revenue")}
							amount={String(Math.abs(revenue))}
							delta={revenue > 0 ? profit / revenue : undefined}
							onClick={() => navigate({ to: "/accounting/statements" })}
						/>
						<KPICard
							label={t("kpi_expenses")}
							amount={String(Math.abs(expenses))}
							onClick={() => navigate({ to: "/accounting/statements" })}
						/>
						<KPICard
							label={t("kpi_cash")}
							amount={String(cash)}
							onClick={() => navigate({ to: "/accounting/bank" })}
						/>
						<KPICard
							label={t("kpi_profit")}
							amount={String(profit)}
							onClick={() => navigate({ to: "/accounting/statements" })}
						/>
						{/* AR with aging bar */}
						<KPICard
							label={t("kpi_ar_total")}
							amount={String(arTotal)}
							onClick={() => navigate({ to: "/accounting/receivables" })}
						>
							<AgingBar
								current={arTotal * 0.6}
								d30={arTotal * 0.2}
								d60={arTotal * 0.1}
								d90={arTotal * 0.05}
								d90plus={arTotal * 0.05}
							/>
						</KPICard>
						{/* AP with aging bar */}
						<KPICard
							label={t("kpi_ap_total")}
							amount={String(apTotal)}
							onClick={() => navigate({ to: "/accounting/payables" })}
						>
							<AgingBar
								current={apTotal * 0.7}
								d30={apTotal * 0.15}
								d60={apTotal * 0.08}
								d90={apTotal * 0.04}
								d90plus={apTotal * 0.03}
							/>
						</KPICard>
					</>
				)}
			</div>

			{/* ── Alerts card ── */}
			<AlertsCard periods={periods} />

			{/* ── Row 2: Autonomy bar ── */}
			{autonomyConfig && (
				<div className="flex items-center gap-4 px-3 py-1.5 text-xs">
					<span className="flex items-center gap-1 text-muted-foreground">
						<ShieldCheck className="h-3 w-3" />
						{t("autonomy_config")}:
					</span>
					{AUTONOMY_LABELS.map(({ key, field }) => (
						<span key={field} className="text-muted-foreground">
							{t(key)}: <strong>L{autonomyConfig[field]}</strong>
						</span>
					))}
				</div>
			)}

			{/* ── Row 3: Main content — 2-column layout ── */}
			<div className="flex-1 grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-0 overflow-auto">
				{/* Column 1: Action items + Period close */}
				<div className="flex flex-col border-r">
					<div className="flex items-center gap-1.5 px-3 py-2 border-b">
						<AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
						<span className="text-xs font-semibold">
							{t("attention_title")}
						</span>
					</div>
					<div className="divide-y border-b">
						<ActionRow
							icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
							label={t("attention_drafts", { count: draftCount })}
							to="/accounting/journal"
							count={draftCount}
						/>
						<ActionRow
							icon={<Banknote className="h-3.5 w-3.5 text-muted-foreground" />}
							label={t("attention_unreconciled", { count: 0 })}
							to="/accounting/bank"
							count={0}
						/>
						<ActionRow
							icon={<Bot className="h-3.5 w-3.5 text-muted-foreground" />}
							label={t("attention_ai_proposed", {
								count: pendingDecisions,
								pct: pendingDecisions > 0 ? 87 : 0,
							})}
							to="/accounting/journal"
							count={pendingDecisions}
						/>
						<ActionRow
							icon={<Clock className="h-3.5 w-3.5 text-muted-foreground" />}
							label={t("attention_overdue_ar", { count: 0 })}
							to="/accounting/receivables"
							count={0}
						/>
					</div>
					<Link
						to="/accounting/close"
						className="flex items-center gap-3 px-3 py-3 border-b hover:bg-accent/50 transition-colors"
					>
						<ProgressRing percent={closePercent} />
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium">
								{t("attention_period_close", { pct: closePercent })}
							</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								{periodId} · {closeDone}/{closeTotal} {t("close_tasks_done")}
							</p>
						</div>
						<ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
					</Link>
					{decisions.length > 0 && (
						<>
							<div className="flex items-center gap-1.5 px-3 py-2 border-b">
								<Bot className="h-3.5 w-3.5 text-primary" />
								<span className="text-xs font-semibold">
									{t("agent_decisions")}
								</span>
								<span className="ml-auto text-[10px] text-muted-foreground bg-muted rounded-full px-1.5 py-0.5">
									{decisions.length}
								</span>
							</div>
							<div className="divide-y max-h-48 overflow-y-auto">
								{decisions.slice(0, 5).map((d) => (
									<div key={d.id} className="flex items-center gap-2 px-3 py-2">
										<div className="flex-1 min-w-0">
											<p className="text-xs truncate">
												{t(`decision_type_${d.decisionType}`, {
													defaultValue: d.decisionType,
												})}
											</p>
											<p className="text-[10px] text-muted-foreground">
												{d.agentName} · {Math.round(d.confidence * 100)}%
											</p>
										</div>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() => approveMutation.mutate({ id: d.id })}
											disabled={approveMutation.isPending}
											className="text-green-600 hover:text-green-700 p-1 rounded hover:bg-green-50 dark:hover:bg-green-950"
										>
											<CheckCircle2 className="h-4 w-4" />
										</Button>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() =>
												rejectMutation.mutate({ id: d.id, reason: "" })
											}
											disabled={rejectMutation.isPending}
											className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50 dark:hover:bg-red-950"
										>
											<XCircle className="h-4 w-4" />
										</Button>
									</div>
								))}
							</div>
						</>
					)}
					{recentItems.length > 0 && (
						<>
							<div className="px-3 py-1.5 border-b text-xs font-semibold">
								{t("recent_viewed", { defaultValue: "Недавние" })}
							</div>
							<div className="divide-y">
								{recentItems.map((item) => (
									<Link
										key={`${item.type}-${item.id}`}
										to={item.path}
										className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-accent/50 transition-colors"
									>
										<span className="text-muted-foreground">
											{item.type === "entry"
												? "JE"
												: item.type === "account"
													? "AC"
													: "RC"}
										</span>
										<span className="truncate">{item.label}</span>
									</Link>
								))}
							</div>
						</>
					)}
				</div>

				{/* Column 2: Recent entries */}
				<div className="flex flex-col">
					<div className="flex-1 flex flex-col">
						<div className="flex items-center justify-between px-3 py-2 border-b">
							<span className="text-xs font-semibold">
								{t("recent_entries")}
							</span>
							<Link
								to="/accounting/journal"
								className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
							>
								{t("view_all")}
								<ArrowRight className="h-3 w-3" />
							</Link>
						</div>
						{entriesLoading ? (
							<div className="p-3 space-y-2">
								{Array.from({ length: 5 }).map((_, i) => (
									<Skeleton key={i} className="h-8 w-full" />
								))}
							</div>
						) : entries.length === 0 ? (
							<div className="flex-1 flex flex-col items-center justify-center gap-2 px-6">
								<p className="text-sm font-medium">
									{t("empty_journal_title", { defaultValue: "Журнал пуст" })}
								</p>
								<p className="text-xs text-muted-foreground text-center">
									{t("empty_journal_desc", {
										defaultValue:
											"Создайте первую проводку, чтобы начать вести учёт",
									})}
								</p>
								<Link
									to="/accounting/journal/new"
									className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
								>
									{t("empty_journal_action", {
										defaultValue: "Создать проводку",
									})}
								</Link>
							</div>
						) : (
							<div className="flex-1 overflow-auto">
								<DataTable
									dense
									columns={dashboardEntryCols}
									data={entries}
									onRowClick={(entry) =>
										navigate({
											to: "/accounting/journal/$id",
											params: { id: entry.id },
										})
									}
									getRowId={(row) => row.id}
									className="border-0 rounded-none"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ActionRow({
	icon,
	label,
	to,
	count,
}: {
	icon: React.ReactNode;
	label: string;
	to: string;
	count: number;
}) {
	return (
		<Link
			to={to}
			className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-accent/50 transition-colors"
		>
			{icon}
			<span className="flex-1 text-muted-foreground">{label}</span>
			{count > 0 && (
				<span className="text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 rounded-full px-1.5 py-0.5">
					{count}
				</span>
			)}
			<ArrowRight className="h-3 w-3 text-muted-foreground" />
		</Link>
	);
}

function EntryStatusDot({
	status,
	t,
}: { status: number; t: (key: string) => string }) {
	const config: Record<number, { color: string; label: string }> = {
		1: { color: "text-gray-400", label: t("status_draft") },
		2: { color: "text-amber-500", label: t("status_pending") },
		3: { color: "text-blue-500", label: t("status_approved") },
		4: { color: "text-green-500", label: t("status_posted") },
		5: { color: "text-red-500", label: t("status_reversed") },
	};
	const c = config[status] ?? { color: "text-gray-400", label: "\u2014" };
	return (
		<span className={`inline-flex items-center gap-1 text-[10px] ${c.color}`}>
			<Circle className="h-2 w-2 fill-current" />
			{c.label}
		</span>
	);
}

function AlertsCard({ periods }: { periods?: { periods: Array<{ periodId: string; status: number }> } }) {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();

	const openPeriods = periods?.periods.filter((p) => p.status === PeriodStatus.OPEN) ?? [];
	const unclosed = periods?.periods.filter(
		(p) => p.status !== PeriodStatus.CLOSED && p.status !== PeriodStatus.OPEN,
	) ?? [];

	const alerts: { icon: typeof AlertTriangle; color: string; text: string; to: string }[] = [];

	if (unclosed.length > 0) {
		alerts.push({
			icon: AlertTriangle,
			color: "text-amber-600",
			text: t("alert_unclosed_periods", { count: unclosed.length }),
			to: "/accounting/close",
		});
	}

	if (openPeriods.length > 1) {
		alerts.push({
			icon: Clock,
			color: "text-blue-600",
			text: t("alert_multiple_open", { count: openPeriods.length }),
			to: "/accounting/close",
		});
	}

	if (alerts.length === 0) return null;

	return (
		<div className="flex items-center gap-3 px-3 py-2 border-b bg-amber-50/50 dark:bg-amber-900/10 shrink-0">
			{alerts.map((a, i) => (
				<Button
					key={i}
					type="button"
					variant="ghost"
					onClick={() => navigate({ to: a.to })}
					className={`flex items-center gap-1.5 text-xs ${a.color} hover:underline`}
				>
					<a.icon className="h-3.5 w-3.5" />
					{a.text}
				</Button>
			))}
		</div>
	);
}
